import {
  indexDDL,
  runManyRows,
  tableDDL,
  type SqliteExecutor,
  type SqlValue,
} from '@/data/sqliteExecutor.ts'
import {
  ENTITY_REPLICA_CONFIGS,
  type EntityReplicaConfig,
  type ReplicaColumnType,
} from '@/data/entityReplicaConfig.generated.ts'
import {
  deleteCollectionMemberships,
  replaceCollectionMemberships,
} from '@/data/userDataTables.ts'

const COLLECTION_ENTITY = 'ReleaseItemCollection'

// A ReleaseItemCollection delta carries `releaseItems { id }` (membership only);
// reconstruct the implicit m2m rows so the offline alt-version branch can join
// owned items to their collections, mirroring the server's _ReleaseItem_collections.
function membershipItemIds(item: DeltaItem): string[] {
  const releaseItems = item.releaseItems
  if (!Array.isArray(releaseItems)) return []
  return releaseItems
    .map((entry) => (entry as { id?: unknown }).id)
    .filter((id): id is string => typeof id === 'string')
}

type Tombstone = { entity: string; rowId: string }
type DeltaItem = Record<string, unknown>

// The catalogChanges page: one array per replicated entity field (only the
// fields the app's GET_CATALOG_CHANGES selects are present) plus the shared
// tombstone list. Entities whose field is absent are simply skipped.
export type DeltaPage = { tombstones: readonly Tombstone[] } & Record<string, unknown>

// Bump happens automatically: the generated version is a hash of the entity's
// columns + projection. On mismatch the replica wipes that entity and
// re-backfills it from seq 0 — see ensureReplicaShape in the catalog-sync store.
export const REPLICA_VERSION: Record<string, number> = Object.fromEntries(
  ENTITY_REPLICA_CONFIGS.map((config) => [config.entity, config.version]),
)

const CONFIG_BY_ENTITY = new Map<string, EntityReplicaConfig>(
  ENTITY_REPLICA_CONFIGS.map((config) => [config.entity, config]),
)

const CURSOR_KEY = 'cursor'

async function getMeta(executor: SqliteExecutor, key: string): Promise<string | undefined> {
  const rows = await executor.query('SELECT value FROM replica_kv WHERE key = ?', [key])
  const value = rows[0]?.value
  return typeof value === 'string' ? value : undefined
}

async function setMeta(executor: SqliteExecutor, key: string, value: string): Promise<void> {
  await executor.run('INSERT OR REPLACE INTO replica_kv (key, value) VALUES (?, ?)', [key, value])
}

export async function getCursor(executor: SqliteExecutor): Promise<string> {
  return (await getMeta(executor, CURSOR_KEY)) ?? '0'
}

export function setCursor(executor: SqliteExecutor, seq: string): Promise<void> {
  return setMeta(executor, CURSOR_KEY, seq)
}

export async function getEntityVersion(
  executor: SqliteExecutor,
  entity: string,
): Promise<number | undefined> {
  const value = await getMeta(executor, `version:${entity}`)
  return value === undefined ? undefined : Number(value)
}

export function setEntityVersion(
  executor: SqliteExecutor,
  entity: string,
  version: number,
): Promise<void> {
  return setMeta(executor, `version:${entity}`, String(version))
}

const SUBSCRIBED_TYPES_KEY = 'subscribedTypes'

// The medium types whose historical slice has already been backfilled into the
// replica. Compared against the live subscription preference to drive scoped
// widen-backfill / narrow-prune; co-located with the data so a wipe resets it too.
export async function getSubscribedTypes(executor: SqliteExecutor): Promise<string[] | undefined> {
  const value = await getMeta(executor, SUBSCRIBED_TYPES_KEY)
  return value === undefined ? undefined : (JSON.parse(value) as string[])
}

export function setSubscribedTypes(executor: SqliteExecutor, types: string[]): Promise<void> {
  return setMeta(executor, SUBSCRIBED_TYPES_KEY, JSON.stringify(types))
}

const COVER_PACK_TIER_KEY = 'coverPackTier'

// The tier whose pack has actually been downloaded into the cover store; compared
// against the live preference to drive pack download / re-download on tier change.
// Co-located with the data so a wiped replica re-seeds the pack too.
export async function getCoverPackTier(executor: SqliteExecutor): Promise<string | undefined> {
  return getMeta(executor, COVER_PACK_TIER_KEY)
}

export function setCoverPackTier(executor: SqliteExecutor, tier: string): Promise<void> {
  return setMeta(executor, COVER_PACK_TIER_KEY, tier)
}


const LAST_SYNCED_KEY = 'lastSyncedAt'

export function getLastSyncedAt(executor: SqliteExecutor): Promise<string | undefined> {
  return getMeta(executor, LAST_SYNCED_KEY)
}

export function setLastSyncedAt(executor: SqliteExecutor, iso: string): Promise<void> {
  return setMeta(executor, LAST_SYNCED_KEY, iso)
}

// Total replicated catalog rows + on-disk size of the whole replica DB
// (page_count * page_size). Powers the Settings "Offline-Daten" storage readout.
export async function getReplicaStorage(
  executor: SqliteExecutor,
): Promise<{ rows: number; bytes: number }> {
  let rows = 0
  for (const config of ENTITY_REPLICA_CONFIGS) {
    const result = await executor.query(`SELECT COUNT(*) AS count FROM "${config.table}"`, [])
    rows += Number(result[0]?.count ?? 0)
  }
  const pageCount = await executor.query('PRAGMA page_count', [])
  const pageSize = await executor.query('PRAGMA page_size', [])
  const freelist = await executor.query('PRAGMA freelist_count', [])
  const livePages = Number(pageCount[0]?.page_count ?? 0) - Number(freelist[0]?.freelist_count ?? 0)
  const bytes = Math.max(livePages, 0) * Number(pageSize[0]?.page_size ?? 0)
  return { rows, bytes }
}

function configFor(entity: string): EntityReplicaConfig {
  const config = CONFIG_BY_ENTITY.get(entity)
  if (!config) throw new Error(`No replica config for entity: ${entity}`)
  return config
}

export async function wipeEntity(executor: SqliteExecutor, entity: string): Promise<void> {
  const config = configFor(entity)
  await executor.run(`DROP TABLE IF EXISTS "${config.table}"`, [])
  await executor.run(tableDDL(config), [])
  for (const statement of indexDDL(config)) await executor.run(statement, [])
}

function coerceForStorage(value: unknown, type: ReplicaColumnType): SqlValue {
  if (type === 'bool') return value ? 1 : 0
  if (value === null || value === undefined) return null
  if (type === 'int') {
    const num = Number(value)
    return Number.isNaN(num) ? null : Math.trunc(num)
  }
  if (type === 'numeric') {
    const num = Number(value)
    return Number.isNaN(num) ? null : num
  }
  return String(value)
}

// A foreign-key column stores the related row's id; the delta carries it as a
// nested `{ id }` object, so unwrap it before coercing.
function fkId(value: unknown): unknown {
  if (value && typeof value === 'object' && 'id' in value) {
    return (value as { id: unknown }).id
  }
  return value
}

function columnValues(config: EntityReplicaConfig, item: DeltaItem): SqlValue[] {
  const values = config.columns.map((column) =>
    coerceForStorage(column.fk ? fkId(item[column.col]) : item[column.col], column.type),
  )
  values.push(JSON.stringify(item))
  return values
}

function upsertSql(config: EntityReplicaConfig): string {
  const cols = [...config.columns.map((column) => `"${column.col}"`), '"data"']
  const placeholders = cols.map(() => '?').join(', ')
  return `INSERT OR REPLACE INTO "${config.table}" (${cols.join(', ')}) VALUES (${placeholders})`
}

function itemsFor(config: EntityReplicaConfig, page: DeltaPage): DeltaItem[] {
  const items = page[config.field]
  return Array.isArray(items) ? (items as DeltaItem[]) : []
}

const DELETE_CHUNK_ROWS = 500

async function deleteRowsById(
  executor: SqliteExecutor,
  table: string,
  ids: readonly string[],
): Promise<void> {
  for (let start = 0; start < ids.length; start += DELETE_CHUNK_ROWS) {
    const chunk = ids.slice(start, start + DELETE_CHUNK_ROWS)
    await executor.run(
      `DELETE FROM "${table}" WHERE id IN (${chunk.map(() => '?').join(', ')})`,
      chunk,
    )
  }
}

// Applies all pages' changes for one entity as bulk statements. Rows are
// aggregated across pages with last-op-wins per id (page order preserved), which
// is what makes cross-page bulking legal: an id upserted then tombstoned ends
// deleted, tombstoned then re-upserted ends present — same final state as
// replaying the pages one by one. Bulking matters on device: each native call is
// a bridge round-trip, so per-page batches (~50 rows) were still bridge-bound.
async function applyPagesForEntity(
  executor: SqliteExecutor,
  config: EntityReplicaConfig,
  pages: readonly DeltaPage[],
): Promise<void> {
  const upsertsById = new Map<string, SqlValue[]>()
  const membershipById = new Map<string, string[]>()
  const deleteIds = new Set<string>()
  for (const page of pages) {
    for (const item of itemsFor(config, page)) {
      if (typeof item.id !== 'string') continue
      upsertsById.set(item.id, columnValues(config, item))
      deleteIds.delete(item.id)
      if (config.entity === COLLECTION_ENTITY) {
        membershipById.set(item.id, membershipItemIds(item))
      }
    }
    for (const tombstone of page.tombstones) {
      if (tombstone.entity !== config.entity) continue
      deleteIds.add(tombstone.rowId)
      upsertsById.delete(tombstone.rowId)
      membershipById.delete(tombstone.rowId)
    }
  }
  if (upsertsById.size > 0) {
    await runManyRows(executor, upsertSql(config), [...upsertsById.values()])
  }
  if (config.entity === COLLECTION_ENTITY) {
    await replaceCollectionMemberships(executor, membershipById)
    await deleteCollectionMemberships(executor, [...deleteIds])
  }
  if (deleteIds.size > 0) {
    await deleteRowsById(executor, config.table, [...deleteIds])
  }
}

export async function applyDelta(executor: SqliteExecutor, page: DeltaPage): Promise<void> {
  await applyDeltaBatch(executor, [page])
}

// Apply many pages in a SINGLE transaction. The web executor flushes the whole
// in-memory DB to IndexedDB once per transaction, so a per-page transaction during
// a bootstrap (hundreds of blob pages) re-serializes the growing DB hundreds of
// times — O(pages × dbSize). One transaction per blob makes it one flush.
export async function applyDeltaBatch(
  executor: SqliteExecutor,
  pages: readonly DeltaPage[],
): Promise<void> {
  await executor.transaction(async () => {
    for (const config of ENTITY_REPLICA_CONFIGS) {
      await applyPagesForEntity(executor, config, pages)
    }
  })
}

export async function prune(executor: SqliteExecutor, types: string[]): Promise<void> {
  if (types.length === 0) return
  // One transaction so a mid-loop crash can't leave some entity tables narrowed
  // and others untouched. VACUUM stays outside — SQLite forbids it in a transaction.
  await executor.transaction(async () => {
    for (const config of ENTITY_REPLICA_CONFIGS) {
      if (!config.columns.some((column) => column.col === 'type')) continue
      await executor.run(
        `DELETE FROM "${config.table}" WHERE type IN (${types.map(() => '?').join(', ')})`,
        types,
      )
    }
  })
  await executor.run('VACUUM', [])
}
